import { createRecordType, defineMigrations } from "@supersig/store";
import { T } from "@supersig/validate";
import { nanoid } from "nanoid";
import { buildAutomatableValue } from "../../primitives/automation";
import { propertyTypeMap } from "../../primitives/literals/properties";
import { idValidator } from "../misc/id-validator";
const notificationTextValue = buildAutomatableValue(propertyTypeMap["property-text"]);
const notificationDelayValue = T.object({
    type: T.literal("literal"),
    value: T.object({
        type: T.literal("number"),
        value: T.number
    })
});
export const PaywallNotificationValidator = T.model("paywall_notification", T.object({
    id: idValidator("paywall_notification"),
    typeName: T.literal("paywall_notification"),
    title: notificationTextValue,
    subtitle: notificationTextValue,
    body: notificationTextValue,
    delay: T.optional(notificationDelayValue),
    before_trial_end: T.optional(notificationDelayValue)
}));
const Versions = {
    StandardizeTypes: 1
};
export const SWPaywallNotificationRecordType = createRecordType("paywall_notification", {
    // Migrate legacy notification text values to property-text type
    migrations: defineMigrations({
        currentVersion: Versions.StandardizeTypes,
        migrators: {
            [Versions.StandardizeTypes]: {
                up: (record)=>{
                    // Migrate text fields from { type: "string" } to { type: "property-text" }
                    const migrateText = (value)=>{
                        if (value?.type === "literal" && value.value?.type === "string") {
                            return {
                                ...value,
                                value: {
                                    type: "property-text",
                                    value: value.value.value,
                                    rendering: {
                                        type: "literal"
                                    }
                                }
                            };
                        }
                        return value;
                    };
                    return {
                        ...record,
                        title: migrateText(record.title),
                        subtitle: migrateText(record.subtitle),
                        body: migrateText(record.body)
                    };
                },
                down: (record)=>{
                    // Migrate text fields back to old string format
                    const downgradeText = (value)=>{
                        if (value?.type === "literal" && value.value?.type === "property-text") {
                            return {
                                ...value,
                                value: {
                                    type: "string",
                                    value: value.value.value
                                }
                            };
                        }
                        return value;
                    };
                    return {
                        ...record,
                        title: downgradeText(record.title),
                        subtitle: downgradeText(record.subtitle),
                        body: downgradeText(record.body)
                    };
                }
            }
        }
    }),
    validator: PaywallNotificationValidator,
    scope: "document"
}).withDefaultProperties(()=>({}));
export function isPaywallNotification(record) {
    if (!record) return false;
    return record.typeName === "paywall_notification";
}
export function isPaywallNotificationId(id) {
    if (!id) return false;
    return id.startsWith("paywall_notification:");
}
/** @public */ export function createPaywallNotificationId(id) {
    return `paywall_notification:${id ?? nanoid()}`;
}
// This is the only notification id we've setup so far
export const trialStartedPaywallNotificationId = createPaywallNotificationId("TRIAL_STARTED");
const createPropertyTextLiteral = (value)=>{
    return {
        type: "property-text",
        value,
        rendering: {
            type: "literal"
        }
    };
};
export const createNotificationLiteralValue = (value)=>({
        type: "literal",
        value: createPropertyTextLiteral(value)
    });
// Legacy export aliases (backward compatibility)
export const createLiteralAutomatableValue = createNotificationLiteralValue;
export const createNotificationDelayLiteralValue = (value)=>({
        type: "literal",
        value: {
            type: "number",
            value: value
        }
    });
export const isNotificationValueDynamic = (value)=>{
    return value.type === "conditional" || value.type === "referential";
};
export const isAutomatableValueDynamic = isNotificationValueDynamic;
/**
 * Extracts a string value from a notification automatable value.
 * For literal values, returns the text directly.
 * For conditional values, returns the fallback option (query: null) or first available option.
 * For referential values, returns empty string (cannot resolve without runtime context).
 */ export const literalTextFromValue = (value)=>{
    if (!value) {
        return "";
    }
    if (value.type === "literal") {
        return value.value.value;
    }
    if (value.type === "conditional") {
        // First, try to find the fallback option (query: null) for old SDKs
        const fallbackOption = value.options.find((opt)=>opt.query === null);
        if (fallbackOption?.value && fallbackOption.value.type === "literal" && fallbackOption.value.value.type === "property-text") {
            return fallbackOption.value.value.value;
        }
        // If no fallback, use the first available option
        for (const option of value.options){
            const optionValue = option.value;
            if (optionValue && optionValue.type === "literal" && optionValue.value.type === "property-text") {
                return optionValue.value.value;
            }
        }
        return "";
    }
    // Unable to resolve referential values without runtime context
    return "";
};
/**
 * Unwraps a number value (in milliseconds) from a literal number wrapper.
 * Returns 0 for undefined/null values.
 */ export const unwrapNumberLiteral = (value)=>{
    if (!value) {
        return 0;
    }
    if (value.type === "literal" && value.value.type === "number") {
        return value.value.value;
    }
    return 0;
};
